import { axios, exportAction } from '@/libs/api.request';
import { Response } from '@/types/request.d';
import { TranslateWay } from '@/types/base/index';
import { clientTool, isClient } from '@/libs/client';
import { HttpReq } from '@/libs/axios';

export interface TranslateParams{
    source_lang?: string;
    target_lang?: string;
    content?: string; // [{"sequence_id": 1, "msg": "hello", msg_type: '1'}]
    chat_user_id: string;
    contents?: string;
}
export interface TranslateTextRes {
    sequence_id: string;
    translate_msg: string;
    error_code: string;
}
export interface TranslateJSONRes {
    sequence_id: string;
    msg_type: Chat.msgType;
    translate_msg: { [x: string]: string };
    error_code: string;
}

type TranslateRes = Response<TranslateTextRes[]|TranslateJSONRes[]>;

export interface GetPictureTextParams {
    chat_user_id: string;
    origin_url: string;
}

export interface ExtractPictureTextParams {
    chat_user_id: string;
    origin_url: string;
    zip_url: string;
}

export interface GetPictureTextResData {
    sys_user_id: string;
    project_id: string;
    chat_user_id:string;
    ocr_platform:string;
    ocr_consume_num: string;
    image_hash: string;
    image_origin_url: string;
    image_zip_url: string;
    text: string;
    request_created: string;
    request_id: string;
    created_time:string;
    updated_time:string;
}
interface GetPictureTextRes {
    data: Array<GetPictureTextResData>
}

// 主要用于 客户端
export const getTranslateList = (data: TranslateParams) => axios.request<TranslateRes>({
    url: '/chat/translation/get-translate-list',
    method: 'post',
    data,
    // mock: true,
});

// 主要用于 客户端
interface SaveTranslateParams {
    source_lang?: string;
    target_lang: string;
    content: string; // [{"sequence_id": 1, "msg": "hello", "msg_type": "1", "translate_msg": "hello"}]
    chat_user_id: string;
    platform: string; // 平台
}

export const saveTranslate = (data: SaveTranslateParams) => axios.request({
    url: '/chat/translation/save-translate',
    method: 'post',
    data,
    // mock: true,
});

interface DetectParams {
    chat_user_id: string;
    sequence_id: string;
    msg_type: string;
    msg: string;
    is_ai_detect?: '0' | '1' // 是否是AI识别语言 0-否 1-是
    is_auto_detect?: '0' | '1'; // 是否自动识别语言场景 0-否 1-是
    customTips?: boolean
}
interface DetectRes {
    lang: string;
    translate_way: string; // 翻译路线
}

function getMaxOverlap(str1: string, str2: string) {
    const dp = Array.from({ length: str1.length + 1 }, () => Array(str2.length + 1).fill(0));
    let maxLen = 0;
    let endIdx = 0;

    for (let i = 1; i <= str1.length; i += 1) {
        for (let j = 1; j <= str2.length; j += 1) {
            if (str1[i - 1] === str2[j - 1]) {
                dp[i][j] = dp[i - 1][j - 1] + 1;
                if (dp[i][j] > maxLen) {
                    maxLen = dp[i][j];
                    endIdx = i;
                }
            }
        }
    }

    const maxSubstr = str1.slice(endIdx - maxLen, endIdx);
    return { maxSubstr, length: maxLen };
}

// 判断字符串是否包含中文
function hasChinese(str: string) {
    return /[\u4E00-\u9FA5]+/g.test(str);
}

function localTranslate<T>(data: TranslateParams): HttpReq<T> {
    return clientTool.translateMsg({
        data,
        limit: 3,
        getData: getTranslateList,
        saveData: saveTranslate,
        interceptorFn: (resp: any, type: string) => {
            // {
            // "source": "阿达",
            // "result": "Adah",
            // "host": "translate.google.com.kw",
            // "date": 1746688630153,
            // "to": "en",
            // "from": "zh-CN"
            // },
            console.log('interceptorFn', resp, type);
            // 是否重试1
            let retry = false;
            if (resp && resp.result && resp.source && resp.from === 'zh-CN' && resp.to !== 'zh-CN') {
                retry = true;
                const { maxSubstr, length } = getMaxOverlap(resp.source, resp.result);
                console.log('retry设置为true', maxSubstr, length);
                if (length && maxSubstr) {
                    retry = hasChinese(maxSubstr);
                    console.log('检测重复项为中文', retry, maxSubstr, length);
                }
                // 打个日志叭!!!!!!!!!!
            }
            return retry;
        },
    }).then((res: T) => {
        return res;
    });
}

/**
 * 桌面端检测翻译语言
 * @param data
 */
function localDetect<T>(data: DetectParams): HttpReq<T> {
    return clientTool.translateLocalDetect(data);
}

/**
 * 翻译文本
 */
export const translate = (data: TranslateParams) => {
    // 如果没有开启翻译
    if (!isClient() || !clientTool.checkTranslate()) {
        return axios.request<TranslateRes>({
            url: '/chat/translation/translate',
            method: 'post',
            data,
            customTips: true,
            // mock: true,
        });
    }

    return localTranslate<TranslateRes>(data);
};

/**
 * 检测文本语言
 */
export const detect = (data: DetectParams) => {
    // 非AI识别语言时，才调用本地检测
    if (data.is_ai_detect !== '1') {
        // 如果没有开启翻译
        if (isClient() && clientTool.translateLocalDetect && clientTool.checkTranslate()) {
            return localDetect<Response<DetectRes>>(data);
        }
    }
    return axios.request<Response<DetectRes>>({
        url: '/chat/translation/detect',
        method: 'post',
        data,
        customTips: data?.customTips ?? false,
    });
};

/**
 * 查询翻译应用是否存在
 */
export const queryTranslateIsExist = (data: {translate_way: string}) => axios.request({
    url: '/chat/translation/query-translate-is-exist',
    method: 'post',
    data,
});

/**
 * 获取翻译方式
 */
export const getTranslateWay = () => axios.request<Response<{ translate_way: TranslateWay }>>({
    url: '/chat/translation/get-translate-way',
    method: 'post',
});

/**
 * 语音转文本
 */
export const getSpeechToText = (data: TranslateParams) => {
    return axios.request<TranslateRes>({
        url: '/chat/translation/speech-to-text',
        method: 'post',
        data,
        customTips: true,
        // mock: true,
    });
};

/**
 * 获取图片提取文字消息
 */
export const getPictureText = (params: GetPictureTextParams) => {
    return axios.request<Response<GetPictureTextRes>>({
        url: '/chat/translation/get-picture-text',
        method: 'get',
        params,
    });
};

/**
 * 获取图片提取文字消息
 */
export const extractPictureText = (data: ExtractPictureTextParams) => {
    return axios.request<Response<GetPictureTextResData>>({
        url: '/chat/translation/extract-picture-text',
        method: 'post',
        data,
    });
};

interface TranslateTestParams {
    target_lang: string;
    text: string;
    prompt: string;
    model: string;
}

interface TranslateTestItem {
    error_code: string
    msg_type: string;
    sequence_id: string
    translate_msg: string;
}

/**
 * AI助手翻译预览
 * @param data
 * @returns
 */
export const translateTestAiModel = (data: TranslateTestParams) => axios.request<Response<TranslateTestItem[]>>({
    url: '/chat/translation/translate-test',
    method: 'post',
    data,
});
